import { useQuery } from "@tanstack/react-query";

import { api } from "@/services/api";

/**
 * React Query hooks for the admin Dashboard feature. These wrap the existing
 * API client methods — the client is unchanged — and own the query keys and
 * caching that previously lived as manual `useEffect` loaders in the page.
 *
 * The dashboard is read-only aggregation, so there are no mutations: switching
 * the selected programme simply changes the `detail(programmeId)` query key,
 * which makes React Query refetch naturally (replacing the suppressed
 * "reload on programme change" effect).
 *
 * Follows the Sources slice template.
 */

interface DashboardWindow {
  assignment_id: string;
  week_number: number;
  assignment_number: number;
  start_date: string;
  deadline: string;
  time_status: string;
  assigned_count: number;
  total_enrolled: number;
  users_need_topics: number;
  submitted_count: number;
}

interface DashboardTimelineEntry {
  week_number: number;
  assignment_number: number;
  assignment_id: string;
  start_date: string;
  deadline: string;
  time_status: string;
  assigned_count: number;
  submitted_count: number;
  total_enrolled: number;
}

export interface AdminDashboardData {
  programme: {
    id: string;
    name: string;
    status: string;
    start_date: string;
    end_date: string;
    total_weeks: number;
    current_week: number;
    enrolled_users: number;
  };
  topic_inventory: {
    ready: number;
    used: number;
    draft: number;
    projected_need: number;
    shortfall: number;
  };
  current_window: DashboardWindow | null;
  next_window: DashboardWindow | null;
  timeline: DashboardTimelineEntry[];
}

export const dashboardKeys = {
  all: ["admin", "dashboard"] as const,
  detail: (programmeId: string) => [...dashboardKeys.all, "detail", programmeId] as const,
};

export function useAdminDashboard(programmeId: string | null) {
  return useQuery<AdminDashboardData>({
    queryKey: dashboardKeys.detail(programmeId ?? ""),
    queryFn: () => api.getAdminDashboard(programmeId as string),
    enabled: Boolean(programmeId),
  });
}
